import { getI18nPageProps } from "@utils/translates";
import type { PageWithLayout } from "@types";
import type { Meta } from "@layouts/page";
import type { GetServerSideProps } from "next";
import Page from "@layouts/page";
import { useTranslations } from "next-intl";
import { setContext } from "@utils/Cookies";
import { getLayout as getDefaultLayout } from "@layouts/default-layout";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import programApi from "@api/program";
import { Program } from "@templates/program";
import { useRouter } from "next/router";
import { toFarsiNumber } from "@utils/digits";
import NotFound from "pages/404";
import { useLayoutContext } from "@context/layout";
import { fetchSingleProgramData } from "lib/ssr/program";
import { ProgramSkeleton } from "@modules/program/program-skeleton";
import dynamic from "next/dynamic";
import { setNotFoundResponse } from "lib/ssr/not-found";

const AdminHeader = dynamic(() => import("@shared/components/admin-header"), {
  ssr: false,
});

const ProgramPage: PageWithLayout = ({ is404 }) => {
  const t = useTranslations("meta");
  const router = useRouter();
  const { slug } = router.query;
  const { isAdmin } = useLayoutContext();

  const { data, error, mutate } = useRequestImmutable(
    typeof slug === "string" ? programApi.getProgram(slug) : undefined
  );

  const meta: Meta = {
    title: toFarsiNumber(data?.data.seoTitle || t("title")),
    description: toFarsiNumber(data?.data.seoDescription || t("desc")),
    image: data?.data.photoUrl,
  };

  if (is404 || error?.error?.code === 404) return <NotFound />;

  return (
    <Page meta={meta}>
      {isAdmin ? (
        <AdminHeader
          cacheSetting={{
            withCache: true,
            action: async () => {
              if (typeof slug !== "string") return;
              await programApi.getProgram(slug).fetch();
              mutate(undefined, { revalidate: true });
            },
          }}
          editSetting={{
            withEdit: true,
            url: `https://faradars.org/admin/panel/programs/${data?.data.id}/edit`,
            title: "ویرایش دروس دانشگاهی",
          }}
        />
      ) : null}
      {!data?.data ? <ProgramSkeleton /> : <Program data={data} />}
    </Page>
  );
};

ProgramPage.getLayout = (page, props) =>
  getDefaultLayout(
    page,
    { ...props },
    {
      footerProps: {
        showAppFooter: false,
        showOrganizationFooter: false,
        showAdsFooter: false,
      },
    }
  );

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const i18nProps = await getI18nPageProps(ctx, "program");
  const { query, res, req } = ctx;
  setContext(ctx);

  const ssrActivated =
    String(req.headers?.["x-ssr-activate"]).toLowerCase() !== "false";
  if (!ssrActivated) {
    return { props: i18nProps };
  }
  try {
    const fallback = await fetchSingleProgramData(query);
    return { props: { ...i18nProps, fallback } };
  } catch (error: any) {
    if (error?.error?.code === 404) {
      setNotFoundResponse(res);
    }
    return { props: { ...i18nProps, is404: error?.error?.code === 404 } };
  }
};

export default ProgramPage;
